import styled from '@emotion/styled';
import { TableCell as _TableCell, Typography } from '@material-ui/core';
import CompareArrows from '@material-ui/icons/CompareArrows';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { ExtractedData, ExtractedValue } from '../../../../utility/extractPeriodicImprovementPerformance';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import mq from '../../../styleUtility/mediaquery';

type Props = {
  data: ExtractedData;
  isImprovementRate?: boolean;
  hasKeyRatings?: boolean;
};

type FormattedCellProps = {
  value?: ExtractedValue;
  isImprovementRate: boolean;
  costValue?: boolean;
};

type TargetCellProps = {
  value?: string;
};

const getImprovementRateColor = (improvementRate: number, costValue: boolean) => {
  const isPositiveValue = Math.sign(improvementRate) === 1;
  const isGoodSign = (isPositiveValue && !costValue) || (!isPositiveValue && costValue);
  return isGoodSign ? 'primary' : 'error';
};

const TargetCell: FC<TargetCellProps> = ({ value }) => {
  return (
    <TableCell align="left">
      {value && (
        <TargetCellContainer>
          <CompareArrows fontSize="small" />
          <TargetTypography noWrap>
            <FormattedMessage id={`performance_report_card.options.comparison_target_key.${value}`} />
          </TargetTypography>
        </TargetCellContainer>
      )}
    </TableCell>
  );
};

const FormattedTableCell: FC<FormattedCellProps> = ({ value, isImprovementRate, costValue = false }) => {
  if (value === undefined) return <TableCell align="right">–</TableCell>;
  const { type, currency, value: cellValue } = value;
  const color = isImprovementRate ? getImprovementRateColor(cellValue, costValue) : 'textPrimary';
  const formatStyle = type === 'percentage' ? 'percent' : type;

  return (
    <TableCell align="right">
      <Typography color={color} noWrap>
        {isImprovementRate && Math.sign(cellValue) === 1 && '+'}
        <FormattedNumber value={cellValue} style={formatStyle} currency={currency} maximumFractionDigits={2} />
      </Typography>
    </TableCell>
  );
};

const ImprovementRateTableCells: FC<Props> = ({ data, hasKeyRatings, isImprovementRate = false }) => {
  const {
    target,
    impressions,
    cost_per_click,
    conversion_rate,
    cost_per_action,
    view_through_cost_per_action,
    click_through_rate,
    video_view_rate_p25,
    video_view_rate_p95,
    video_view_rate_p100,
  } = data;

  return (
    <>
      <TargetCell value={target} />
      <FormattedTableCell value={impressions} isImprovementRate={isImprovementRate} />
      <FormattedTableCell value={click_through_rate} isImprovementRate={isImprovementRate} />
      <FormattedTableCell value={cost_per_click} isImprovementRate={isImprovementRate} costValue />
      <FormattedTableCell value={conversion_rate} isImprovementRate={isImprovementRate} />
      <FormattedTableCell value={cost_per_action} isImprovementRate={isImprovementRate} costValue />
      <FormattedTableCell value={view_through_cost_per_action} isImprovementRate={isImprovementRate} costValue />
      <FormattedTableCell value={video_view_rate_p25} isImprovementRate={isImprovementRate} />
      {hasKeyRatings ? (
        <FormattedTableCell value={video_view_rate_p95} isImprovementRate={isImprovementRate} />
      ) : (
        <FormattedTableCell value={video_view_rate_p100} isImprovementRate={isImprovementRate} />
      )}
    </>
  );
};

export const TableCellStyle = {
  padding: `0 ${SPACING_UNIT}px`,
};

export const CreativeCell = styled(_TableCell)(TableCellStyle, {
  paddingLeft: SPACING_REGULAR,
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: '#fff',

  [mq('sp')]: {
    position: 'initial',
  },
});

export const TableCell = styled(_TableCell)(TableCellStyle, {});

const TargetCellContainer = styled('div')({
  display: 'inline-flex',
  verticalAlign: 'middle',
});

const TargetTypography = styled(Typography)({
  paddingLeft: SPACING_UNIT / 2,
});

export default ImprovementRateTableCells;
